/**
 * @fileoverview LoginSettings - Login and password policy configuration
 * Uses view/edit mode pattern with SettingsViewEdit component
 * Manages authentication settings, password policies, session settings, and biometric login
 */
import React, { useCallback } from 'react';
import {
    useGet,
    intl,
    fetchData,
    HTTP_STATUS
} from 'dmsbasereact';
import { Box, Typography, Grid } from '@mui/material';
import { SettingsViewEdit } from '../../components/SettingsLayouts';
import Swal from 'sweetalert2';

/**
 * View mode content - displays all settings as read-only
 */
const ViewContent = ({ data }) => {
    const passwordFormatLabels = {
        4: intl.get('PasswordFormatSimple').d('Simple (min 8 chars)'),
        5: intl.get('PasswordFormatMedium').d('Medium (min 8 chars + letters + numbers)'),
        7: intl.get('PasswordFormatStrong').d('Strong (min 8 chars + upper + lower + number + symbol)')
    };

    return (
        <Box>
            {/* Password Policy Section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    {intl.get('PasswordPolicy').d('Password Policy')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('MaxLoginAttempts').d('Limit of login attempts')}
                        </Typography>
                        <Typography variant="body1">{data.max_qt_tries ?? 5}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('PasswordExpiration').d('Password expiration in days')}
                        </Typography>
                        <Typography variant="body1">{data.password_expiration ?? 90}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('PasswordExpirationNotify').d('Days to notify before expiration')}
                        </Typography>
                        <Typography variant="body1">{data.password_expiration_notify ?? 7}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('PasswordFormat').d('Password format')}
                        </Typography>
                        <Typography variant="body1">
                            {passwordFormatLabels[data.password_format_pk] || passwordFormatLabels[7]}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('PasswordHistoryCount').d('Password history count')}
                        </Typography>
                        <Typography variant="body1">{data.password_history_count ?? 3}</Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* Session Settings Section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    {intl.get('SessionSettings').d('Session Settings')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('TokenExpiration').d('Token expiration time (seconds)')}
                        </Typography>
                        <Typography variant="body1">{data.token_expiration ?? 3600}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('InactivityTime').d('Inactivity time (seconds)')}
                        </Typography>
                        <Typography variant="body1">{data.inactivity_time ?? 1800}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('MaxUserTokens').d('Max tokens per user')}
                        </Typography>
                        <Typography variant="body1">{data.max_user_tokens ?? 5}</Typography>
                    </Grid>
                </Grid>
            </Box>

        </Box>
    );
};

/**
 * Edit mode content - displays all settings as form fields
 */
const EditContent = ({ data, formData, onFieldChange }) => {
    const passwordFormatOptions = [
        { value: 4, label: intl.get('PasswordFormatSimple').d('Simple (min 8 chars)') },
        { value: 5, label: intl.get('PasswordFormatMedium').d('Medium (min 8 chars + letters + numbers)') },
        { value: 7, label: intl.get('PasswordFormatStrong').d('Strong (min 8 chars + upper + lower + number + symbol)') }
    ];

    return (
        <Box>
            {/* Password Policy Section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    {intl.get('PasswordPolicy').d('Password Policy')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {intl.get('MaxLoginAttempts').d('Limit of login attempts')}
                        </Typography>
                        <input
                            type="number"
                            value={formData.max_qt_tries ?? 5}
                            onChange={(e) => onFieldChange('max_qt_tries', parseInt(e.target.value, 10))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontSize: '14px'
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {intl.get('MaxLoginAttemptsHelp').d('Number of failed login attempts before account lockout')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {intl.get('PasswordExpiration').d('Password expiration in days')}
                        </Typography>
                        <input
                            type="number"
                            value={formData.password_expiration ?? 90}
                            onChange={(e) => onFieldChange('password_expiration', parseInt(e.target.value, 10))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontSize: '14px'
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {intl.get('PasswordExpirationHelp').d('Days until password must be changed (0 = never)')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {intl.get('PasswordExpirationNotify').d('Days to notify before expiration')}
                        </Typography>
                        <input
                            type="number"
                            value={formData.password_expiration_notify ?? 7}
                            onChange={(e) => onFieldChange('password_expiration_notify', parseInt(e.target.value, 10))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontSize: '14px'
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {intl.get('PasswordExpirationNotifyHelp').d('Notify users this many days before password expires')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {intl.get('PasswordFormat').d('Password format')}
                        </Typography>
                        <select
                            value={formData.password_format_pk ?? 7}
                            onChange={(e) => onFieldChange('password_format_pk', parseInt(e.target.value, 10))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontSize: '14px'
                            }}
                        >
                            {passwordFormatOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {intl.get('PasswordFormatHelp').d('Password complexity requirements')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {intl.get('PasswordHistoryCount').d('Password history count')}
                        </Typography>
                        <input
                            type="number"
                            min="0"
                            value={formData.password_history_count ?? 3}
                            onChange={(e) => onFieldChange('password_history_count', parseInt(e.target.value, 10))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontSize: '14px'
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {intl.get('PasswordHistoryCountHelp').d('Number of previous passwords that cannot be reused (0 = disabled)')}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* Session Settings Section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    {intl.get('SessionSettings').d('Session Settings')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {intl.get('TokenExpiration').d('Token expiration time (seconds)')}
                        </Typography>
                        <input
                            type="number"
                            step="0.1"
                            value={formData.token_expiration ?? 3600}
                            onChange={(e) => onFieldChange('token_expiration', parseFloat(e.target.value))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontSize: '14px'
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {intl.get('TokenExpirationHelp').d('Duration before authentication token expires')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {intl.get('InactivityTime').d('Inactivity time (seconds)')}
                        </Typography>
                        <input
                            type="number"
                            step="0.1"
                            value={formData.inactivity_time ?? 1800}
                            onChange={(e) => onFieldChange('inactivity_time', parseFloat(e.target.value))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontSize: '14px'
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {intl.get('InactivityTimeHelp').d('Logout user after this many seconds of inactivity')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {intl.get('MaxUserTokens').d('Max tokens per user')}
                        </Typography>
                        <input
                            type="number"
                            value={formData.max_user_tokens ?? 5}
                            onChange={(e) => onFieldChange('max_user_tokens', parseInt(e.target.value, 10))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontSize: '14px'
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {intl.get('MaxUserTokensHelp').d('Maximum number of concurrent sessions per user')}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

        </Box>
    );
};

/**
 * LoginSettings - Main component
 * Manages login and password policy settings using view/edit mode pattern
 */
export const LoginSettings = () => {
    const { data, isLoading, refetch } = useGet('/login_settings', null, true);

    /**
     * Prepare form data from backend data
     */
    const prepareFormData = useCallback((data) => {
        return {
            max_qt_tries: data.max_qt_tries ?? 5,
            password_expiration: data.password_expiration ?? 90,
            password_expiration_notify: data.password_expiration_notify ?? 7,
            token_expiration: data.token_expiration ?? 3600,
            inactivity_time: data.inactivity_time ?? 1800,
            password_format_pk: data.password_format_pk ?? 7,
            max_user_tokens: data.max_user_tokens ?? 5,
            password_history_count: data.password_history_count ?? 3
        };
    }, []);

    /**
     * Build payload for save operation
     */
    const buildPayload = useCallback((formData) => {
        return {
            max_qt_tries: formData.max_qt_tries,
            password_expiration: formData.password_expiration,
            password_expiration_notify: formData.password_expiration_notify,
            token_expiration: formData.token_expiration,
            inactivity_time: formData.inactivity_time,
            password_format_pk: formData.password_format_pk,
            max_user_tokens: formData.max_user_tokens,
            password_history_count: formData.password_history_count
        };
    }, []);

    /**
     * Save handler - makes PUT request to backend
     */
    const handleSave = useCallback(async (payload) => {
        try {
            const response = await fetchData('/login_settings', 'PUT', true, payload, false, true);

            if (response.status === HTTP_STATUS.OK_200) {
                await Swal.fire({
                    title: intl.get('Success').d('Success'),
                    text: intl.get('SettingsSaved').d('Settings saved successfully'),
                    icon: 'success',
                    confirmButtonText: intl.get('OK').d('OK')
                });
                return response.data;
            } else {
                await Swal.fire({
                    title: intl.get('Error').d('Error'),
                    text: response.message || intl.get('SaveError').d('Failed to save settings'),
                    icon: 'error',
                    confirmButtonText: intl.get('OK').d('OK')
                });
                return null;
            }
        } catch (error) {
            await Swal.fire({
                title: intl.get('Error').d('Error'),
                text: error.message || intl.get('SaveError').d('Failed to save settings'),
                icon: 'error',
                confirmButtonText: intl.get('OK').d('OK')
            });
            return null;
        }
    }, []);

    return (
        <Box>
            <SettingsViewEdit
                data={data?.data}
                loading={isLoading}
                title={intl.get('LoginSettings').d('Login Settings')}
                description={intl.get('LoginSettingsDescription').d(
                    'Configure authentication settings, password policies, session management, and biometric login options.'
                )}
                viewContent={ViewContent}
                editContent={EditContent}
                prepareFormData={prepareFormData}
                buildPayload={buildPayload}
                onSave={handleSave}
                onDataUpdated={refetch}
                confirmTitle={intl.get('ConfirmSaveLoginSettings').d('Confirm Save Login Settings')}
                confirmMessage={intl.get('ConfirmSaveLoginSettingsMessage').d(
                    'Are you sure you want to save these login settings? This will affect authentication and password policies for all users.'
                )}
            />
        </Box>
    );
};
